import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { invalidateUserCache } from "./useUser";

export interface ApertureSession {
  environment_pub_id: string;
  aperture_refresh_url: string;
  session_expires_at: string;
  provider: string;
  token: string;
}

export type ApertureSessions = Record<string, ApertureSession>;

export const APERTURE_SESSIONS_KEY = "aperture_sessions";

export function readApertureSessions(): ApertureSessions {
  try {
    const raw = localStorage.getItem(APERTURE_SESSIONS_KEY);
    if (raw) return JSON.parse(raw) as ApertureSessions;
  } catch {
    // localStorage might throw in private browsing mode, or data might be corrupt
  }
  return {};
}

export function writeApertureSessions(sessions: ApertureSessions): void {
  localStorage.setItem(APERTURE_SESSIONS_KEY, JSON.stringify(sessions));
}

function removeApertureSession(environmentPubId: string): void {
  const sessions = readApertureSessions();
  delete sessions[environmentPubId];
  writeApertureSessions(sessions);
}

interface OsoJwtMessage {
  type: "oso:jwt";
  token: string;
}

function isOsoJwtMessage(data: unknown): data is OsoJwtMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    (data as Record<string, unknown>).type === "oso:jwt" &&
    "token" in data &&
    typeof (data as Record<string, unknown>).token === "string"
  );
}

/**
 * Creates a hidden iframe that loads `aperture_refresh_url?jwt=...&next_url=...`.
 * Aperture verifies the expiring JWT, mints a fresh one, and 302-redirects
 * (within the iframe) to `next_url` — a lightweight callback page on the Oso
 * origin that relays the token back via `{ type: "oso:jwt", token }` postMessage.
 *
 * Returns a promise that resolves with the fresh JWT token. The iframe and
 * message listener are cleaned up automatically when the promise settles or
 * the signal aborts. Rejects with `TimeoutError` on timeout, `AbortError`
 * on external cancellation.
 */
function requestJwtViaIframe(
  session: ApertureSession,
  signal: AbortSignal
): Promise<string> {
  if (signal.aborted) {
    return Promise.reject(signal.reason);
  }

  const iframe = document.createElement("iframe");
  iframe.hidden = true;
  const refreshUrl = new URL(session.aperture_refresh_url);
  refreshUrl.searchParams.set("jwt", session.token);
  refreshUrl.searchParams.set(
    "next_url",
    new URL(
      "/aperture-refresh-callback.html",
      window.location.origin
    ).toString()
  );
  iframe.src = refreshUrl.toString();
  iframe.sandbox.add("allow-scripts");
  // Chrome's Local Network Access gates iframe loads to private/local
  // addresses behind a permission prompt. Delegate the permission so
  // the refresh iframe can reach Aperture on the tailnet or localhost.
  // https://docs.google.com/document/d/1QQkqehw8umtAgz5z0um7THx-aoU251p705FbIQjDuGs/edit?tab=t.0#heading=h.denb9idl4lm0
  iframe.allow = "local-network-access *; local-network *; loopback-network *";
  document.body.appendChild(iframe);

  return new Promise<string>((resolve, reject) => {
    let teardownAbort: (() => void) | undefined;

    const cleanup = () => {
      clearTimeout(timeoutId);
      teardownAbort?.();
      window.removeEventListener("message", listener);
      iframe.remove();
    };

    const listener = (event: MessageEvent<unknown>) => {
      if (event.source !== iframe.contentWindow) return;
      if (!isOsoJwtMessage(event.data)) return;
      cleanup();
      resolve(event.data.token);
    };

    const timeoutId = setTimeout(() => {
      cleanup();
      reject(new DOMException("Signal timed out.", "TimeoutError"));
    }, 30_000);

    const onAbort = () => {
      cleanup();
      reject(signal.reason);
    };
    signal.addEventListener("abort", onAbort, { once: true });
    teardownAbort = () => signal.removeEventListener("abort", onAbort);

    window.addEventListener("message", listener);
  });
}

interface RefreshResponse {
  session_expires_at: string;
}

interface SessionRefreshState {
  expiryTimeout: NodeJS.Timeout | null;
  refreshAbort: AbortController | null;
}

/**
 * Manages Aperture session refresh lifecycle via iframe + postMessage.
 *
 * Tracks multiple sessions keyed by `environment_pub_id`, each with its own
 * independent refresh timer, iframe, and message listener.
 *
 * Why iframe-based refresh: Aperture's backend is tailnet-only (not
 * internet-routable), so Oso's server can't call it directly. The user's
 * browser can reach both networks, so we embed a hidden iframe that sends the
 * expiring JWT to Aperture's refresh endpoint. Aperture verifies it, mints a
 * fresh JWT, and 302-redirects back to a lightweight callback page on the Oso
 * origin (`/aperture-refresh-callback.html`). The callback page relays the
 * fresh token to the parent window via postMessage.
 *
 * postMessage protocol: the callback page sends `{ type: "oso:jwt", token }` to
 * the parent window.
 *
 * `provider` is read from localStorage (not hardcoded) to support future JWT
 * providers that may have different refresh flows.
 *
 * Lifecycle (per session):
 * - Reads all sessions from localStorage on mount
 * - Sets a refresh timer for shortly before each session's `session_expires_at`
 * - When timer fires, creates a hidden iframe to request a fresh JWT
 * - Receives the fresh JWT via postMessage from the callback page
 * - Updates that session in localStorage and resets timer on success
 * - Shows toast and clears that session on timeout (30s with no valid message)
 * - Cleans up all sessions on unmount
 */
export function useApertureSession(): void {
  const stateMapRef = useRef<Map<string, SessionRefreshState>>(new Map());

  useEffect(() => {
    const stateMap = stateMapRef.current;

    const sessions = readApertureSessions();

    // If no sessions, this is a no-op (user didn't come through JWT auth)
    if (Object.keys(sessions).length === 0) return;

    // Used to cancel pending lock requests on unmount
    const abortController = new AbortController();

    const getOrCreateState = (envPubId: string): SessionRefreshState => {
      let state = stateMap.get(envPubId);
      if (!state) {
        state = {
          expiryTimeout: null,
          refreshAbort: null,
        };
        stateMap.set(envPubId, state);
      }
      return state;
    };

    const cleanupSession = (envPubId: string) => {
      const state = stateMap.get(envPubId);
      if (!state) return;

      if (state.expiryTimeout) {
        clearTimeout(state.expiryTimeout);
        state.expiryTimeout = null;
      }
      if (state.refreshAbort) {
        state.refreshAbort.abort();
        state.refreshAbort = null;
      }
      stateMap.delete(envPubId);
    };

    const scheduleRefresh = (
      session: ApertureSession,
      { silent = false }: { silent?: boolean } = {}
    ) => {
      const envPubId = session.environment_pub_id;
      const state = getOrCreateState(envPubId);

      // Clear any existing refresh timer
      if (state.expiryTimeout) {
        clearTimeout(state.expiryTimeout);
        state.expiryTimeout = null;
      }

      const expiresAt = new Date(session.session_expires_at).getTime();
      if (isNaN(expiresAt)) {
        console.error(
          "Invalid session_expires_at value:",
          session.session_expires_at
        );
        try {
          removeApertureSession(envPubId);
        } catch (err) {
          console.error("Failed to clear aperture session:", err);
        }
        return;
      }
      const refreshAt = expiresAt - 30 * 1000; // 30 seconds before expiry
      const timeUntilRefresh = refreshAt - Date.now();

      if (timeUntilRefresh <= 0) {
        triggerRefresh(session, { silent });
      } else {
        state.expiryTimeout = setTimeout(
          () => triggerRefresh(session, { silent }),
          timeUntilRefresh
        );
      }
    };

    const scheduleExpiryInvalidation = (
      session: ApertureSession,
      state: SessionRefreshState
    ) => {
      if (state.expiryTimeout) clearTimeout(state.expiryTimeout);
      const envPubId = session.environment_pub_id;
      const msUntilExpiry =
        new Date(session.session_expires_at).getTime() - Date.now();
      state.expiryTimeout = setTimeout(() => {
        invalidateUserCache();
        cleanupSession(envPubId);
      }, Math.max(0, msUntilExpiry));
    };

    const doRefresh = async (
      session: ApertureSession,
      { silent = false }: { silent?: boolean } = {}
    ): Promise<void> => {
      const envPubId = session.environment_pub_id;
      const state = getOrCreateState(envPubId);

      // Abort any in-flight refresh for this session
      if (state.refreshAbort) {
        state.refreshAbort.abort();
        state.refreshAbort = null;
      }

      state.refreshAbort = new AbortController();

      let token: string;
      try {
        token = await requestJwtViaIframe(session, state.refreshAbort.signal);
      } catch (err) {
        state.refreshAbort = null;

        // External abort (unmount or superseded) — silent, no further action
        if (err instanceof DOMException && err.name === "AbortError") return;

        // Timeout — iframe never sent a valid JWT.
        if (!silent) {
          toast.info("Session expiring — reconnect from Aperture to continue", {
            position: "top-center",
          });
        }
        // Schedule a cache invalidation at session expiry so useUser's 401
        // handler fires and redirects to /aperture. Sessions stay in
        // localStorage for Aperture-user detection; logout() clears them.
        scheduleExpiryInvalidation(session, state);
        return;
      }

      state.refreshAbort = null;

      try {
        const response = await fetch("/web/auth/jwt", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ provider: session.provider, token }),
          signal: abortController.signal,
        });
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const data: RefreshResponse = await response.json();

        const newSession: ApertureSession = {
          environment_pub_id: envPubId,
          aperture_refresh_url: session.aperture_refresh_url,
          session_expires_at: data.session_expires_at,
          provider: session.provider,
          token,
        };
        try {
          const current = readApertureSessions();
          current[envPubId] = newSession;
          writeApertureSessions(current);
        } catch (err) {
          console.error(
            "Failed to update aperture session in localStorage:",
            err
          );
        }

        cleanupSession(envPubId);
        scheduleRefresh(newSession);
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.error("Failed to refresh session:", err);
        if (!silent) {
          toast.error("Session refresh failed", {
            position: "top-center",
          });
        }
        scheduleExpiryInvalidation(session, state);
      }
    };

    const triggerRefresh = async (
      session: ApertureSession,
      { silent = false }: { silent?: boolean } = {}
    ) => {
      // Fall back to unlocked refresh if Web Locks API is unavailable
      if (!navigator.locks) {
        await doRefresh(session, { silent });
        return;
      }

      const envPubId = session.environment_pub_id;
      // Web Locks: only one tab refreshes per environment
      // https://caniuse.com/mdn-api_navigator_locks
      const lockName = `aperture-refresh:${envPubId}`;

      try {
        await navigator.locks.request(
          lockName,
          { ifAvailable: true },
          async (lock) => {
            if (!lock) {
              // Another tab is already refreshing this environment. Wait for
              // that tab to finish, then reschedule from localStorage.
              await navigator.locks.request(
                lockName,
                { signal: abortController.signal },
                () => {
                  const sessions = readApertureSessions();
                  const updated = sessions[envPubId];
                  if (updated) scheduleRefresh(updated);
                  // If session was removed (refresh failed in other tab), do nothing —
                  // user will see the toast from the tab that failed.
                }
              );
              return;
            }

            // We hold the lock. Do the refresh; lock releases when promise resolves.
            await doRefresh(session, { silent });
          }
        );
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.error("Failed to acquire lock for aperture refresh:", err);
      }
    };

    // Schedule refresh for all sessions. First attempt is silent since we don't
    // yet know if the session is still live (e.g., stale localStorage).
    for (const session of Object.values(sessions)) {
      scheduleRefresh(session, { silent: true });
    }

    // Cleanup on unmount
    return () => {
      abortController.abort();
      stateMap.forEach((_state, envPubId) => {
        cleanupSession(envPubId);
      });
      stateMap.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally mount-only
  }, []);
}
